import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useTheme, useMediaQuery, Box, TextField, Button,  
    CircularProgress, Typography, FormControlLabel, Checkbox, 
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const RolePage = () => {
    useAuthCheck();
    const navigate = useNavigate();
    const { roleId } = useParams();    
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [formValues, setFormValues] = useState({
        role: '',
        description: '',
        isActive: true,
    });        

    
    useEffect(() => {
        const fetchRole = async () => {
            try {
                setLoading(true);

                if (roleId) {
                    const res = await api.get(`/roles/${roleId}`);

                    setFormValues({
                        role: res.data.role || '',
                        description: res.data.description || '',                        
                        isActive: res.data.isActive !== undefined ? res.data.isActive : true,
                    });
                }
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        fetchRole();
    }, [roleId]);

    const checkoutSchema = yup.object().shape({
        role: yup.string().required('Required'),
    });

    const handleFormSubmit = async (values, onSubmitProps) => {
        try {
            const method = roleId ? "put" : "post";
            const url = roleId
                ? `/roles/update/${roleId}`
                : '/roles/new';

            await api({
                method,
                url,
                data: {
                    role: values.role,
                    description: values.description,
                    isActive: values.isActive,
                },
            });

            toast.success(roleId 
                ? "Role updated successfully." 
                : "Role created successfully."
            );

            // onSubmitProps.resetForm(); 
            navigate("/roles");
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to submit form!");
            setError(error);
        }
    };


    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="Role"
                    subtitle={`${loading ? 'Loading...' : roleId ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/roles")}
                        >
                            <ArrowBackIosNewIcon />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>

            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>                
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <Formik
                        initialValues={formValues}
                        enableReinitialize={true}
                        validationSchema={checkoutSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Box
                                    display="grid"
                                    gap={2}
                                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                    sx={{
                                        '& > div': { gridColumn: isDesktop ? undefined : 'span 12' },
                                    }}
                                >
                                    <TextField
                                        fullWidth
                                        required
                                        variant="filled"
                                        type="text"
                                        label="Role"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.role}
                                        name="role"
                                        error={!!touched.role && !!errors.role}
                                        helperText={touched.role && errors.role}
                                        sx={{ gridColumn: 'span 12' }}
                                    />

                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        multiline
                                        rows={3}
                                        label="Description"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        value={values.description}
                                        name="description"
                                        error={!!touched.description && !!errors.description}
                                        helperText={touched.description && errors.description}
                                        sx={{ gridColumn: 'span 12' }}
                                    />

                                    <FormControlLabel
                                        sx={{ gridColumn: 'span 12' }}
                                        control={
                                            <Checkbox
                                                checked={values.isActive}
                                                onChange={handleChange}
                                                name="isActive"
                                                color="primary"
                                            />
                                        }
                                        label="Is Active"
                                    />
                                    
                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ gridColumn: "span 12", height: 42 }}
                                    >
                                        {roleId ? "Update" : "Create"}
                                    </Button>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            )}
        </Box>
    );
};

export default RolePage;
